// src/components/UserMeet/BreakoutOverlay.jsx
import React, { useState, useMemo, useCallback } from "react";
import {
  LiveKitRoom,
  RoomAudioRenderer,
  useTracks,
  useLocalParticipant,
  useParticipants,
} from "@livekit/components-react";
import { Track } from "livekit-client";
import { toast } from "sonner";
import Icon from "@/pages/MeetingRoom/ui/Icon";
import LiveKitVideo from "@/pages/MeetingRoom/ui/LiveKitVideo";
import { getAvatarColors } from "@/pages/MeetingRoom/utils/avatar";
import BreakoutTimerBanner from "./BreakoutTimerBanner";
import InviteParticipantsModal from "./InviteParticipantsModal";

/* ─────────────────────────────────────────────
   Grid layout helper
   - 1 person  → full screen
   - 2 people  → side by side (50/50)
   - 3 people  → 1 top full + 2 bottom
   - 4+        → auto grid
───────────────────────────────────────────── */
function getGridStyle(count) {
  if (count === 1) {
    return {
      display: "grid",
      gridTemplateColumns: "1fr",
      gridTemplateRows: "1fr",
    };
  }
  if (count === 2) {
    return {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gridTemplateRows: "1fr",
    };
  }
  if (count === 3) {
    return {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gridTemplateRows: "1fr 1fr",
    };
  }
  // 4+
  const cols = Math.ceil(Math.sqrt(count));
  return {
    display: "grid",
    gridTemplateColumns: `repeat(${cols}, 1fr)`,
    gridTemplateRows: `repeat(${Math.ceil(count / cols)}, 1fr)`,
  };
}

/* ─────────────────────────────────────────────
   INNER component — runs inside LiveKitRoom ctx
───────────────────────────────────────────── */
function BreakoutRoomInner({
  isOwner,
  storeParticipants, // from Zustand (name/image/isOwner metadata)
  expiresAt,
  ownerDisconnected,
  gracePeriodSeconds,
  onLeave,
  onEnd,
  onInvite,
  inviteLoading,
  mainMeetingParticipants,
  myUserId,
  timeLimitMinutes,
}) {
  const [showInvite, setShowInvite] = useState(false);

  // ── LiveKit hooks ──────────────────────────────────────────────────
  const { localParticipant } = useLocalParticipant();

  // useParticipants() returns ALL participants including local
  // We use it as the ground truth for WHO is actually in the LiveKit room
  const allLiveKitParticipants = useParticipants();

  const micOn = localParticipant?.isMicrophoneEnabled ?? false;
  const camOn = localParticipant?.isCameraEnabled ?? false;

  // ── Mic / Cam toggles ─────────────────────────────────────────────
  const toggleMic = useCallback(async () => {
    if (!localParticipant) return;
    try {
      await localParticipant.setMicrophoneEnabled(!micOn);
    } catch (err) {
      console.error("Failed to toggle mic:", err);
      toast.error("Failed to toggle microphone");
    }
  }, [localParticipant, micOn]);

  const toggleCam = useCallback(async () => {
    if (!localParticipant) return;
    try {
      await localParticipant.setCameraEnabled(!camOn);
    } catch (err) {
      console.error("Failed to toggle cam:", err);
      toast.error("Failed to toggle camera");
    }
  }, [localParticipant, camOn]);

  // ── Camera tracks — NO placeholders ───────────────────────────────
  const cameraTracks = useTracks(
    [{ source: Track.Source.Camera, withPlaceholder: false }],
    { onlySubscribed: false },
  );

  const cameraMap = useMemo(() => {
    const m = new Map();
    (cameraTracks || []).forEach((t) => {
      if (t?.participant?.identity) {
        m.set(t.participant.identity, t);
      }
    });
    return m;
  }, [cameraTracks]);

  // ── Build a metadata lookup from Zustand store ────────────────────
  // key: userId string → { name, image, isOwner }
  const storeMeta = useMemo(() => {
    const m = new Map();
    for (const p of storeParticipants || []) {
      if (p.userId) m.set(String(p.userId), p);
    }
    return m;
  }, [storeParticipants]);

  // ── FINAL participant list ─────────────────────────────────────────
  //
  // SOURCE OF TRUTH = LiveKit participants (useParticipants)
  // This ensures BOTH host and participant see everyone actually
  // connected to the LiveKit room — no one is missed.
  //
  // We enrich each LiveKit participant with metadata from the store
  // (name, image, isOwner) when available.
  //
  const enrichedParticipants = useMemo(() => {
    // Collect all LiveKit participants (remote = from useParticipants,
    // local = from useLocalParticipant)
    const lkParticipants = [];

    // Add local participant first
    if (localParticipant) {
      lkParticipants.push(localParticipant);
    }

    // Add remote participants (useParticipants already includes remote only
    // when used inside LiveKitRoom, but let's be safe and deduplicate)
    for (const rp of allLiveKitParticipants || []) {
      if (rp.identity !== localParticipant?.identity) {
        lkParticipants.push(rp);
      }
    }

    return lkParticipants.map((lkp) => {
      // Extract userId from LiveKit identity
      // Identity format from backend: "breakout_{userId}_{timestamp}"
      const identityParts = (lkp.identity || "").split("_");

      // Try to extract userId: identity is "breakout_<userId>_<ts>"
      // so userId is at index 1
      let extractedUserId = null;
      if (identityParts.length >= 3 && identityParts[0] === "breakout") {
        extractedUserId = identityParts[1];
      }

      // Also try matching myUserId for local participant
      if (lkp.isLocal && myUserId) {
        extractedUserId = extractedUserId || String(myUserId);
      }

      // Look up store metadata
      const meta = extractedUserId ? storeMeta.get(extractedUserId) : null;

      // Camera track
      const trackRef = cameraMap.get(lkp.identity) || null;
      const pub = trackRef?.publication ?? null;
      const hasVideo = !!(
        lkp.isCameraEnabled &&
        pub?.track &&
        pub?.isMuted === false
      );

      // Determine display name
      // Priority: store name > livekit participant name > identity
      const displayName = meta?.name || lkp.name || lkp.identity || "Unknown";

      // Determine isOwner
      // Priority: store data > local participant + isOwner prop
      const participantIsOwner =
        meta?.isOwner ?? (lkp.isLocal ? isOwner : false);

      return {
        identity: lkp.identity,
        userId: extractedUserId || lkp.identity,
        name: displayName,
        image: meta?.image || null,
        isLocal: !!lkp.isLocal,
        isOwner: participantIsOwner,
        isMuted: lkp.isMicrophoneEnabled === false,
        isCameraOn: lkp.isCameraEnabled !== false,
        isSpeaking: !!lkp.isSpeaking,
        trackRef,
        hasVideo,
      };
    });
  }, [
    localParticipant,
    allLiveKitParticipants,
    storeMeta,
    cameraMap,
    myUserId,
    isOwner,
  ]);

  const count = enrichedParticipants.length;
  const gridStyle = getGridStyle(count);

  // ── Render ────────────────────────────────────────────────────────
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(6,35,23,0.97)",
        zIndex: 9990,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <RoomAudioRenderer />

      {/* Timer banner */}
      <BreakoutTimerBanner
        expiresAt={expiresAt}
        ownerDisconnected={ownerDisconnected}
        gracePeriodSeconds={gracePeriodSeconds}
        visible={true}
      />

      {/* ── Header ── */}
      <div
        style={{
          padding: "14px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          background: "rgba(0,0,0,0.35)",
          backdropFilter: "blur(12px)",
          flexShrink: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 18 }}>🔒</span>
          <div>
            <div style={{ fontWeight: 700, color: "#fff", fontSize: 15 }}>
              Private Meeting
            </div>
            <div style={{ fontSize: 12, opacity: 0.6, color: "#fff" }}>
              {count} participant{count !== 1 ? "s" : ""}
              {" · "}
              {timeLimitMinutes} min limit
            </div>
          </div>
        </div>

        {/* Invite button — owner only */}
        {isOwner && (
          <button
            type="button"
            onClick={() => setShowInvite(true)}
            style={{
              padding: "8px 16px",
              borderRadius: 10,
              border: "1px solid rgba(74,222,128,0.4)",
              background: "rgba(74,222,128,0.1)",
              color: "#4ADE80",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: 13,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Icon name="people" />
            Invite
          </button>
        )}
      </div>

      {/* ── Video Grid ── */}
      <div
        style={{
          flex: 1,
          padding: count === 1 ? 0 : 12,
          ...gridStyle,
          gap: count === 1 ? 0 : 10,
          overflow: "hidden",
          minHeight: 0,
        }}
      >
        {enrichedParticipants.map((p, idx) => {
          const [c1, c2] = getAvatarColors(p.name);

          // For 3 participants, make first tile span full width
          const tileStyle =
            count === 3 && idx === 0 ? { gridColumn: "1 / -1" } : {};

          return (
            <div
              key={p.identity}
              style={{
                ...tileStyle,
                borderRadius: count === 1 ? 0 : 14,
                overflow: "hidden",
                background: `linear-gradient(135deg, ${c1}22, ${c2}22)`,
                border:
                  count === 1
                    ? "none"
                    : p.isLocal
                      ? "2px solid rgba(74,222,128,0.5)"
                      : p.isSpeaking
                        ? "2px solid rgba(74,222,128,0.8)"
                        : "1px solid rgba(255,255,255,0.1)",
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 0,
                // Speaking glow
                boxShadow:
                  p.isSpeaking && count > 1
                    ? "0 0 0 2px rgba(74,222,128,0.5)"
                    : "none",
                transition: "box-shadow 0.2s ease",
              }}
            >
              {/* Video */}
              {p.hasVideo && p.trackRef ? (
                <LiveKitVideo
                  trackRef={p.trackRef}
                  muted={p.isLocal}
                  mirror={p.isLocal}
                  objectFit="cover"
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                  }}
                />
              ) : (
                /* Avatar */
                <div
                  style={{
                    width: count === 1 ? 120 : 72,
                    height: count === 1 ? 120 : 72,
                    borderRadius: "50%",
                    background: `linear-gradient(135deg, ${c1}, ${c2})`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: count === 1 ? 44 : 28,
                    fontWeight: 700,
                    color: "#fff",
                    overflow: "hidden",
                    flexShrink: 0,
                  }}
                >
                  {p.image ? (
                    <img
                      src={p.image}
                      alt=""
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    (p.name?.[0] || "?").toUpperCase()
                  )}
                </div>
              )}

              {/* Name tag */}
              <div
                style={{
                  position: "absolute",
                  bottom: count === 1 ? 12 : 8,
                  left: count === 1 ? 16 : 8,
                  right: count === 1 ? 16 : 8,
                  background: "rgba(0,0,0,0.65)",
                  backdropFilter: "blur(6px)",
                  padding: "5px 12px",
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 6,
                }}
              >
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  {p.isLocal ? "You" : p.name}
                  {p.isOwner && (
                    <span
                      style={{
                        fontSize: 10,
                        color: "#FCD34D",
                        fontWeight: 700,
                        padding: "1px 6px",
                        borderRadius: 99,
                        background: "rgba(252,211,77,0.15)",
                        border: "1px solid rgba(252,211,77,0.3)",
                      }}
                    >
                      Host
                    </span>
                  )}
                </span>

                <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                  {p.isMuted && <span style={{ fontSize: 12 }}>🔇</span>}
                  {!p.isCameraOn && <span style={{ fontSize: 12 }}>📷</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Controls bar ── */}
      <div
        style={{
          padding: "14px 20px",
          borderTop: "1px solid rgba(255,255,255,0.08)",
          background: "rgba(0,0,0,0.55)",
          backdropFilter: "blur(12px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 12,
          flexWrap: "wrap",
          flexShrink: 0,
        }}
      >
        {/* Mic */}
        <button
          type="button"
          onClick={toggleMic}
          title={micOn ? "Mute" : "Unmute"}
          style={{
            width: 52,
            height: 52,
            borderRadius: 14,
            border: "1px solid",
            borderColor: micOn
              ? "rgba(74,222,128,0.4)"
              : "rgba(251,113,133,0.5)",
            background: micOn
              ? "rgba(74,222,128,0.15)"
              : "rgba(251,113,133,0.15)",
            color: micOn ? "#4ADE80" : "#FB7185",
            cursor: "pointer",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 3,
          }}
        >
          <Icon name={micOn ? "mic" : "micOff"} />
          <span style={{ fontSize: 10, fontWeight: 600 }}>
            {micOn ? "Mute" : "Unmute"}
          </span>
        </button>

        {/* Camera */}
        <button
          type="button"
          onClick={toggleCam}
          title={camOn ? "Camera off" : "Camera on"}
          style={{
            width: 52,
            height: 52,
            borderRadius: 14,
            border: "1px solid",
            borderColor: camOn
              ? "rgba(74,222,128,0.4)"
              : "rgba(251,113,133,0.5)",
            background: camOn
              ? "rgba(74,222,128,0.15)"
              : "rgba(251,113,133,0.15)",
            color: camOn ? "#4ADE80" : "#FB7185",
            cursor: "pointer",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 3,
          }}
        >
          <Icon name={camOn ? "cam" : "camOff"} />
          <span style={{ fontSize: 10, fontWeight: 600 }}>
            {camOn ? "Camera" : "No Cam"}
          </span>
        </button>

        {/* Divider */}
        <div
          style={{
            width: 1,
            height: 40,
            background: "rgba(255,255,255,0.15)",
            margin: "0 4px",
          }}
        />

        {/* Leave — shown to EVERYONE */}
        <button
          type="button"
          onClick={onLeave}
          title="Leave private meeting"
          style={{
            padding: "0 20px",
            height: 52,
            borderRadius: 14,
            border: "1px solid rgba(251,113,133,0.4)",
            background: "rgba(251,113,133,0.12)",
            color: "#FB7185",
            cursor: "pointer",
            fontWeight: 700,
            fontSize: 14,
            display: "flex",
            alignItems: "center",
            gap: 8,
            transition: "all 0.18s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(251,113,133,0.22)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(251,113,133,0.12)";
          }}
        >
          <Icon name="leave" />
          Leave
        </button>

        {/* End for All — OWNER only */}
        {isOwner && (
          <button
            type="button"
            onClick={onEnd}
            title="End meeting for everyone"
            style={{
              padding: "0 20px",
              height: 52,
              borderRadius: 14,
              border: "none",
              background: "linear-gradient(135deg, #FB7185, #BE123C)",
              color: "#fff",
              cursor: "pointer",
              fontWeight: 700,
              fontSize: 14,
              display: "flex",
              alignItems: "center",
              gap: 8,
              boxShadow: "0 4px 14px rgba(190,18,60,0.4)",
              transition: "all 0.18s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow =
                "0 6px 20px rgba(190,18,60,0.6)";
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow =
                "0 4px 14px rgba(190,18,60,0.4)";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            <Icon name="leave" />
            End for All
          </button>
        )}
      </div>

      {/* Invite Modal */}
      <InviteParticipantsModal
        open={showInvite}
        onClose={() => setShowInvite(false)}
        onInvite={(ids) => {
          onInvite?.(ids);
          setShowInvite(false);
        }}
        loading={inviteLoading}
        participants={mainMeetingParticipants}
        breakoutParticipants={enrichedParticipants}
        myUserId={myUserId}
      />
    </div>
  );
}

/* ─────────────────────────────────────────────
   OUTER component — manages LiveKitRoom
───────────────────────────────────────────── */
export default function BreakoutOverlay({
  open,
  livekitToken,
  livekitServerUrl,
  isOwner,
  participants,
  expiresAt,
  timeLimitMinutes,
  ownerDisconnected,
  gracePeriodSeconds,
  onLeave,
  onEnd,
  onInvite,
  inviteLoading,
  mainMeetingParticipants,
  myUserId,
}) {
  if (!open || !livekitToken || !livekitServerUrl) return null;

  // Normalise wss URL
  const serverUrl = (() => {
    let url = livekitServerUrl;
    if (window.location.protocol === "https:") {
      if (url.startsWith("ws://")) url = url.replace(/^ws:\/\//, "wss://");
      else if (url.startsWith("http://"))
        url = url.replace(/^http:\/\//, "https://");
    }
    return url;
  })();

  return (
    <LiveKitRoom
      key={livekitToken}
      serverUrl={serverUrl}
      token={livekitToken}
      connect={open}
      audio={true}
      video={true}
      onDisconnected={() => {
        console.log("Breakout LiveKit room disconnected");
      }}
      data-lk-theme="default"
      options={{ adaptiveStream: true, dynacast: true }}
      style={{ display: "contents" }}
    >
      <BreakoutRoomInner
        isOwner={isOwner}
        storeParticipants={participants}
        expiresAt={expiresAt}
        timeLimitMinutes={timeLimitMinutes}
        ownerDisconnected={ownerDisconnected}
        gracePeriodSeconds={gracePeriodSeconds}
        onLeave={onLeave}
        onEnd={onEnd}
        onInvite={onInvite}
        inviteLoading={inviteLoading}
        mainMeetingParticipants={mainMeetingParticipants}
        myUserId={myUserId}
      />
    </LiveKitRoom>
  );
}
